/*
 * The API formats every date with `ToString("yyyy-MM-dd")` under the REQUEST CULTURE, and
 * `ar-SA` carries the UmAlQura calendar — so an Arabic reply says `1448-03-04` where an
 * English one says `2026-08-17`. Screens that only PRINT a date pass it through untouched;
 * `policiesService`, `reportsService` and `notificationsService` all say so in place.
 *
 * A form that has to post the date BACK cannot do that. The commands bind a real `DateTime`,
 * `1448-03-04` deserialises as the year 1448, and SQL Server's `datetime` starts at 1753 —
 * so an edit form filled from a list endpoint would fail to save, or worse, save a date
 * fourteen centuries off. This is the inverse those forms need.
 */

const DAY = 86_400_000

/** 1 Muharram 1 AH. The search below starts here and corrects itself, so it only has to be close. */
const EPOCH = Date.UTC(622, 6, 16)

const MEAN_YEAR = 354.367
const MEAN_MONTH = 29.531

/** Latin digits and a fixed zone, so neither the reader's locale nor their clock can shift a day. */
const UMALQURA = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura-nu-latn', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  timeZone: 'UTC',
})

/** `parseInt`, not `Number`: the year part can carry its era — `1448 AH`. */
function hijriPartsOf(ms) {
  const parts = UMALQURA.formatToParts(new Date(ms))
  const value = (type) => parseInt(parts.find((part) => part.type === type)?.value ?? '', 10)
  return { year: value('year'), month: value('month'), day: value('day') }
}

const isoOf = (ms) => new Date(ms).toISOString().slice(0, 10)

const pad = (value) => String(value).padStart(2, '0')

/**
 * A date the server sent, as a Gregorian `yyyy-MM-dd` the pickers and the commands both take.
 *
 * Converts only what is plainly Hijri, so a caller can hand over whatever arrived without
 * first working out which calendar it is in: an English reply, an ISO timestamp and an empty
 * field all come back unharmed.
 */
export function hijriToIso(text) {
  const match = /^(\d{3,4})-(\d{1,2})-(\d{1,2})/.exec(String(text ?? '').trim())
  if (!match) return ''

  const [, y, m, d] = match
  const year = Number(y)
  const month = Number(m)
  const day = Number(d)

  /* Gregorian already — the English build, or any field the server sent raw. */
  if (year >= 1700) return `${year}-${pad(month)}-${pad(day)}`

  let ms = EPOCH + Math.round((year - 1) * MEAN_YEAR + (month - 1) * MEAN_MONTH + (day - 1)) * DAY

  /* The mean-length estimate is a month or two out at this distance; each pass measures its
     own error in days and steps by it, which closes the gap in two or three rounds. */
  for (let pass = 0; pass < 6; pass += 1) {
    const got = hijriPartsOf(ms)
    const drift =
      (year - got.year) * MEAN_YEAR + (month - got.month) * MEAN_MONTH + (day - got.day)
    if (Math.abs(drift) < 1) break
    ms += Math.round(drift) * DAY
  }

  /* UmAlQura months are 29 or 30 days by observation, not by formula, so the estimate can
     still sit a day or two off a real month boundary. This walks outward for the exact hit. */
  for (let step = 0; step <= 3; step += 1) {
    for (const offset of step === 0 ? [0] : [step, -step]) {
      const at = ms + offset * DAY
      const got = hijriPartsOf(at)
      if (got.year === year && got.month === month && got.day === day) return isoOf(at)
    }
  }

  return isoOf(ms)
}
